import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { END_NODE_STEP } from "@redotech/redo-return-ui/flows/return-flow/end-node";
import {
  getPhaseLabel,
  PHASE_ANCHOR_STEP,
  PHASE_ORDER,
} from "@redotech/redo-return-ui/flows/return-flow/phase-anchor";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Team } from "@redotech/redo-team-common/team";
import { Edge, Node } from "@xyflow/react";
import { MutableRefObject } from "react";
import { WarningStep } from "../../components/flow-left-panel";
import { NodeData } from "../../components/flow-step-node";
import { PhaseDividerNodeData } from "../../components/phase-divider-node";
import { safeToModel } from "../validation/safe-to-model";
import { getLayoutedElements } from "./layout";

/**
 * Node payload union for the new flow editor canvas. Step nodes carry
 * `NodeData`; phase swimlane dividers carry `PhaseDividerNodeData`. React
 * Flow discriminates by the `type` field at render time.
 */
export type FlowEditorNodeData = NodeData | PhaseDividerNodeData;

const PHASE_LANE_MIN_HEIGHT = 280;
const PHASE_LANE_X = 360;
const PHASE_LANE_TOP_PADDING = 60;
const PHASE_LANE_BOTTOM_PADDING = 60;
const PHASE_NODE_ESTIMATED_HEIGHT = 120;
const PHASE_NODE_ESTIMATED_WIDTH = 220;
const PHASE_LANE_X_MIN = -200;
const PHASE_LANE_X_MAX = 1200;
const PHASE_DIVIDER_WIDTH = 2400;
/**
 * X coordinate around which both the swimlane nodes and the phase divider
 * (with its label) are centered. Keeping the divider symmetric around this
 * value means React Flow's `fitView` ends up showing the nodes vertically
 * aligned with the phase labels, rather than offset to one side.
 */
const PHASE_CENTER_X = PHASE_LANE_X + PHASE_NODE_ESTIMATED_WIDTH / 2;

interface PhaseLayout {
  positions: Map<string, { x: number; y: number }>;
  phaseBounds: Map<string, { y: number; height: number }>;
  phaseOfNode: Map<string, string>;
  anchorByPhase: Map<string, StepId>;
}

/**
 * Safely extract the phase from a step if it's a phase anchor. Returns
 * undefined for any other step type (including newly-dragged steps whose
 * `toModel` may throw — `safeToModel` swallows the throw).
 */
function getPhaseAnchorPhase(step: Step): string | undefined {
  const model = safeToModel(step);
  return model?.type === PHASE_ANCHOR_STEP ? model.phase : undefined;
}

/**
 * Detect the synthetic End marker. End nodes live in the last phase, are
 * never persisted, and must not be dragged or deleted.
 */
function isEndNode(step: Step): boolean {
  return safeToModel(step)?.type === END_NODE_STEP;
}

/**
 * For Compensation-style flows that contain phase anchor steps, lay each
 * phase's sub-graph in its own vertical lane. Walk downstream from each
 * anchor to gather its descendants, then stack lanes top-to-bottom in
 * canonical phase order (approval / in_transit / delivered). Lane heights
 * expand based on each phase's content so larger sub-graphs fit.
 */
function layoutPhaseSwimlanes(
  steps: Map<StepId, Step>,
  nodes: Node<NodeData>[],
): PhaseLayout | null {
  const anchorByPhase = new Map<string, StepId>();
  for (const [stepId, step] of steps) {
    const phase = getPhaseAnchorPhase(step);
    if (phase && !anchorByPhase.has(phase)) {
      anchorByPhase.set(phase, stepId);
    }
  }
  if (anchorByPhase.size === 0) {
    return null;
  }

  const phaseByStep = new Map<StepId, string>();
  // Pre-mark every phase anchor with its own phase so the BFS below treats
  // *other* anchors as barriers — when a Condition's False branch connects
  // to the next phase's anchor, we must NOT pull that next-phase anchor (or
  // its downstream) into the current phase.
  const allAnchorIds = new Set<StepId>(anchorByPhase.values());
  for (const [phase, anchorId] of anchorByPhase) {
    phaseByStep.set(anchorId, phase);
  }
  // Force End nodes into the last phase so they render inside the Delivered
  // swimlane (no model edge points at them so BFS never finds them).
  const lastPhase = PHASE_ORDER[PHASE_ORDER.length - 1];
  if (lastPhase) {
    for (const [stepId, step] of steps) {
      if (isEndNode(step)) {
        phaseByStep.set(stepId, lastPhase);
      }
    }
  }
  for (const phase of PHASE_ORDER) {
    const anchorId = anchorByPhase.get(phase);
    if (!anchorId) {
      continue;
    }
    // Seed from the anchor's downstream — the anchor itself is already
    // marked above, and we want to traverse only its phase's sub-graph.
    const queue: StepId[] = [];
    const anchorStep = steps.get(anchorId);
    if (anchorStep) {
      for (const ds of anchorStep.downstream()) {
        if (ds.id && !allAnchorIds.has(ds.id)) {
          queue.push(ds.id);
        }
      }
    }
    while (queue.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const id = queue.shift()!;
      if (phaseByStep.has(id)) {
        continue;
      }
      if (allAnchorIds.has(id)) {
        continue;
      }
      phaseByStep.set(id, phase);
      const step = steps.get(id);
      if (!step) {
        continue;
      }
      for (const ds of step.downstream()) {
        if (ds.id && !phaseByStep.has(ds.id) && !allAnchorIds.has(ds.id)) {
          queue.push(ds.id);
        }
      }
    }
  }

  const nodesByPhase = new Map<string, Node<NodeData>[]>();
  for (const node of nodes) {
    const phase = phaseByStep.get(node.id);
    if (!phase) {
      continue;
    }
    if (!nodesByPhase.has(phase)) {
      nodesByPhase.set(phase, []);
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    nodesByPhase.get(phase)!.push(node);
  }

  const positions = new Map<string, { x: number; y: number }>();
  const phaseBounds = new Map<string, { y: number; height: number }>();
  let cumulativeY = 0;
  for (const phase of PHASE_ORDER) {
    const phaseNodes = nodesByPhase.get(phase);
    if (!phaseNodes || phaseNodes.length === 0) {
      continue;
    }
    const anchorId = anchorByPhase.get(phase);
    const anchorNode = phaseNodes.find((n) => n.id === anchorId);
    // Within a phase, preserve dagre's relative offsets. Shift so the anchor
    // sits at (PHASE_LANE_X, cumulativeY + PHASE_LANE_TOP_PADDING).
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const refX = anchorNode?.position.x ?? phaseNodes[0]!.position.x;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const refY = anchorNode?.position.y ?? phaseNodes[0]!.position.y;
    const xOffset = PHASE_LANE_X - refX;
    const yOffset = cumulativeY + PHASE_LANE_TOP_PADDING - refY;

    const endNodes = phaseNodes.filter((n) => {
      const step = steps.get(n.id);
      return step ? isEndNode(step) : false;
    });
    const nonEndNodes = phaseNodes.filter((n) => !endNodes.includes(n));

    let maxNodeBottom = 0;
    for (const node of nonEndNodes) {
      const newY = node.position.y + yOffset;
      positions.set(node.id, { x: node.position.x + xOffset, y: newY });
      maxNodeBottom = Math.max(
        maxNodeBottom,
        newY + PHASE_NODE_ESTIMATED_HEIGHT,
      );
    }

    // End nodes sit centered below the rest of the phase's content. They
    // never have model edges, so dagre's auto position would put them at the
    // origin — pin them explicitly instead.
    for (const node of endNodes) {
      const endY = maxNodeBottom + 40;
      positions.set(node.id, { x: PHASE_LANE_X, y: endY });
      maxNodeBottom = endY + PHASE_NODE_ESTIMATED_HEIGHT;
    }

    const contentHeight =
      maxNodeBottom - cumulativeY + PHASE_LANE_BOTTOM_PADDING;
    const phaseHeight = Math.max(PHASE_LANE_MIN_HEIGHT, contentHeight);
    phaseBounds.set(phase, { y: cumulativeY, height: phaseHeight });
    cumulativeY += phaseHeight;
  }

  return { positions, phaseBounds, phaseOfNode: phaseByStep, anchorByPhase };
}

function calculateStartNodePosition(
  savedPositions: Map<string, { x: number; y: number }>,
): { x: number; y: number } {
  if (savedPositions.size === 0) {
    return { x: 0, y: 0 };
  }

  const positions = Array.from(savedPositions.values());
  const minY = Math.min(...positions.map((p) => p.y));

  const topRowNodes = positions.filter((p) => Math.abs(p.y - minY) < 50);

  const avgX =
    topRowNodes.length > 0
      ? topRowNodes.reduce((sum, p) => sum + p.x, 0) / topRowNodes.length
      : positions.reduce((sum, p) => sum + p.x, 0) / positions.length;

  return { x: avgX, y: minY - 150 };
}

export interface PhaseLayoutSummary {
  /** Step id → phase name for steps reachable from a phase anchor. */
  phaseOfNode: Map<string, string>;
  /** Phase name → step id of that phase's anchor. */
  anchorByPhase: Map<string, string>;
  /** Phase name → Y/height of that phase's lane in the rendered canvas. */
  phaseBounds: Map<string, { y: number; height: number }>;
}

export function createNodesFromSteps(
  steps: Map<StepId, Step>,
  flowType: FlowType,
  userNodePositions: Map<string, { x: number; y: number }>,
  selectedNodeIdsRef: MutableRefObject<Set<string>>,
  team?: Team,
  warningSteps?: WarningStep[],
  /**
   * Positions from a prior render keyed by step id. Used as the orphan
   * fallback: when a step that previously had a swimlane position becomes
   * unreachable from any phase anchor (e.g. an edge was re-routed mid-edit),
   * we pin it where it was last seen instead of letting dagre re-place it
   * somewhere arbitrary like the top-right corner.
   */
  lastKnownPositions?: Map<string, { x: number; y: number }>,
  isStepLocked?: (stepId: StepId) => boolean,
  hasLockedSection?: boolean,
): {
  nodes: Node<FlowEditorNodeData>[];
  nodePositions: Map<string, { x: number; y: number }>;
  phaseLayout?: PhaseLayoutSummary;
} {
  const nodes: Node<NodeData>[] = [];
  const basicEdges: Edge[] = [];
  const nodePositions = new Map<string, { x: number; y: number }>();

  const validStepIds = new Set(steps.keys());

  for (const [stepId, step] of steps) {
    const warningStep = warningSteps?.find((d) => d.title === step.title());
    const endNode = isEndNode(step);
    const phaseAnchor = getPhaseAnchorPhase(step) !== undefined;
    const redoManaged = isStepLocked?.(stepId) ?? false;
    // Start's outgoing edge into the managed entry is locked, so badge it too.
    const locked = redoManaged || (stepId === "start" && !!hasLockedSection);
    const inert = endNode || phaseAnchor;
    nodes.push({
      id: stepId,
      type: "flowStep",
      position: { x: 0, y: 0 },
      data: {
        step,
        stepId,
        flowType,
        allSteps: steps,
        nodePositions,
        team,
        warningMessage: warningStep?.tooltip,
        redoManaged: locked,
      },
      deletable: stepId !== "start" && !inert && !redoManaged,
      draggable: !inert,
      selectable: !inert,
      focusable: !inert,
      selected: false,
    });

    const downstreams = step.downstream();
    for (const downstream of downstreams) {
      if (downstream.id && validStepIds.has(downstream.id)) {
        basicEdges.push({
          id: `${stepId}-${downstream.id}`,
          source: stepId,
          target: downstream.id,
          type: "smoothstep",
        });
      }
    }
  }

  const nonStartStepIds = Array.from(steps.keys()).filter(
    (id) => id !== "start",
  );
  const allNonStartNodesHaveSavedPositions = nonStartStepIds.every((stepId) =>
    userNodePositions.has(stepId),
  );

  // Always run dagre + swimlane so phase bounds are available even when
  // user-saved positions exist. Phase Y bounds need to be enforced on every
  // render regardless of whether the user has dragged nodes — otherwise a
  // step dropped near a phase boundary could drift into the wrong lane.
  const layoutedElements = getLayoutedElements(nodes, basicEdges);
  const autoLayoutNodes = layoutedElements.nodes;
  const swimlaneLayout = layoutPhaseSwimlanes(steps, autoLayoutNodes);

  const startNodePosition =
    allNonStartNodesHaveSavedPositions && nonStartStepIds.length > 0
      ? userNodePositions.get("start") ||
        calculateStartNodePosition(userNodePositions)
      : undefined;

  const positionedNodes = autoLayoutNodes.map((node) => {
    const userPosition = userNodePositions.get(node.id);
    const swimlanePosition = swimlaneLayout?.positions.get(node.id);
    const dagrePosition = node.position;
    const phase = swimlaneLayout?.phaseOfNode.get(node.id);
    const bounds = phase ? swimlaneLayout?.phaseBounds.get(phase) : undefined;

    const cachedPosition = lastKnownPositions?.get(node.id);
    // Use the position cache only while the user is mid-edit (i.e. at least
    // one node has a user-saved position). When `userNodePositions` is empty
    // it means either a fresh load or the user just clicked Auto align — in
    // both cases we want dagre's fresh layout to win instead of the cached
    // positions from the previous render. Without this gate, Auto align is a
    // no-op (cache feeds the old positions back in) and newly dropped nodes
    // can shift around mid-edit. The cache still serves its original purpose
    // as an orphan fallback for swimlane editors (Compensation), where the
    // swimlane branch above wins for in-phase steps and the cache covers
    // out-of-phase ones.
    const useCache = userNodePositions.size > 0;
    let candidatePosition: { x: number; y: number };
    if (node.id === "start" && startNodePosition) {
      candidatePosition = startNodePosition;
    } else if (userPosition) {
      candidatePosition = userPosition;
    } else if (swimlanePosition) {
      candidatePosition = swimlanePosition;
    } else if (useCache && cachedPosition) {
      candidatePosition = cachedPosition;
    } else {
      candidatePosition = dagrePosition;
    }

    // Clamp Y to the phase's vertical band so a step can't drift into a
    // neighboring lane regardless of how it got there (drag overshoot, stale
    // saved position from a prior layout, etc.).
    const finalPosition = bounds
      ? {
          x: candidatePosition.x,
          y: Math.max(
            bounds.y + 16,
            Math.min(
              candidatePosition.y,
              bounds.y + bounds.height - PHASE_NODE_ESTIMATED_HEIGHT,
            ),
          ),
        }
      : candidatePosition;

    nodePositions.set(node.id, finalPosition);

    const shouldBeSelected = selectedNodeIdsRef.current.has(node.id);

    const extent = bounds
      ? ([
          [PHASE_LANE_X_MIN, bounds.y + 16],
          [
            PHASE_LANE_X_MAX,
            bounds.y + bounds.height - PHASE_NODE_ESTIMATED_HEIGHT,
          ],
        ] as [[number, number], [number, number]])
      : undefined;

    return {
      ...node,
      position: finalPosition,
      selected: shouldBeSelected,
      ...(extent ? { extent } : {}),
    };
  });

  const phaseLayoutSummary: PhaseLayoutSummary | undefined = swimlaneLayout
    ? {
        phaseOfNode: swimlaneLayout.phaseOfNode,
        anchorByPhase: swimlaneLayout.anchorByPhase,
        phaseBounds: swimlaneLayout.phaseBounds,
      }
    : undefined;

  // Inject non-interactive phase divider nodes for Compensation-style flows.
  const dividerNodes = buildPhaseDividerNodes(steps, positionedNodes);
  const allNodes: Node<FlowEditorNodeData>[] =
    dividerNodes.length > 0
      ? [...dividerNodes, ...positionedNodes]
      : [...positionedNodes];

  return { nodes: allNodes, nodePositions, phaseLayout: phaseLayoutSummary };
}

function buildPhaseDividerNodes(
  steps: Map<StepId, Step>,
  positionedNodes: Node<NodeData>[],
): Node<PhaseDividerNodeData>[] {
  const phasesPresent = new Set<string>();
  const anchorIdByPhase = new Map<string, string>();
  for (const [stepId, step] of steps) {
    const phase = getPhaseAnchorPhase(step);
    if (!phase) {
      continue;
    }
    phasesPresent.add(phase);
    if (!anchorIdByPhase.has(phase)) {
      anchorIdByPhase.set(phase, stepId);
    }
  }
  if (phasesPresent.size === 0) {
    return [];
  }

  // Compute each phase's top Y from the positioned anchor node so dividers
  // track the actual rendered layout (which may include dynamic phase
  // heights or user-saved positions).
  const phaseTopY = new Map<string, number>();
  for (const phase of PHASE_ORDER) {
    const anchorId = anchorIdByPhase.get(phase);
    if (!anchorId) {
      continue;
    }
    const anchorNode = positionedNodes.find((n) => n.id === anchorId);
    if (anchorNode) {
      phaseTopY.set(phase, anchorNode.position.y - 40);
    }
  }

  const dividers: Node<PhaseDividerNodeData>[] = [];
  for (const phase of PHASE_ORDER) {
    if (!phasesPresent.has(phase)) {
      continue;
    }
    const phaseTop = phaseTopY.get(phase);
    if (phaseTop === undefined) {
      continue;
    }
    dividers.push({
      id: `phase-divider-${phase}`,
      type: "phaseDivider",
      position: { x: PHASE_CENTER_X - PHASE_DIVIDER_WIDTH / 2, y: phaseTop },
      data: { label: getPhaseLabel(phase), width: PHASE_DIVIDER_WIDTH },
      draggable: false,
      selectable: false,
      deletable: false,
      focusable: false,
    });
  }
  return dividers;
}
